import { deepEqual } from 'fast-equals';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import { useQuery } from 'react-query';
import { array, object, string } from 'yup';
import {
  getAddressBookItems,
  getFinalContractNotification,
  getFinalPriceContractNotification,
  getInvitationDetails,
  getOfferContractNotification,
  sendFinalContractUploadedEmail,
  sendFinalPriceContractUploadedEmail,
  sendOfferContractUploadedEmail,
  sendUnderContractClosingInvitationEmail,
  sendUnderContractEmailUpdates,
  updateUnderContractClosingInvitationEmail
} from '../../../../../fetch';
import { formatDate, formatShortFriendlyDateWithTime } from '../../../../../helpers';
import {
  EmailType,
  EventType,
  IAddressBookItem,
  IContractAcceptedNotification,
  IContractDatesValues,
  IFinalContractNotification,
  IFinalPriceContractNotification,
  IInvitationRes,
  IUnderContractEmailPayload
} from '../../../../../models';
import { underContractPages } from '../under-contract-details';
import { UnderContractEmailModalContent } from './under-contract-email-modal-content';
import { isPast } from 'date-fns';

interface IUnderContractEmailModal {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  buildId: string;
  fetchClientHistory?: () => void;
  selectedPage?: underContractPages;
  emailType: string;
  contractDates?: IContractDatesValues;
  refetchContractDetails?: () => void;
  eventType?: string;
}

const Schema = object().shape({
  emailTemplate: string(),
  subject: string().required('An email subject is required'),
  emailAddresses: array()
    .of(string().email('invalid email address'))
    .min(1, 'At least 1 recipient is required')
    .required('Required'),
  message: string().when('emailType', {
    is: 'Email',
    then: string().required('An email message is required'),
    otherwise: string()
  }),
  location: string().when('emailType', {
    is: 'Invite',
    then: string().when('eventType', {
      is: (eventType: EventType) =>
        eventType === EventType.HomeownerOrientation ||
        eventType === EventType.HomeownerCelebration,
      then: string(),
      otherwise: string().required('Location is required')
    }),
    otherwise: string()
  })
});

export const UnderContractEmailModal: FC<IUnderContractEmailModal> = ({
  open,
  onClose,
  setIsLoading,
  isLoading,
  buildId,
  fetchClientHistory,
  selectedPage,
  emailType,
  contractDates,
  refetchContractDetails,
  eventType
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isLoadingInvitationDetails, data: invitationDetails } = useQuery<
    IInvitationRes,
    Error
  >(
    ['invitationDetails', open, eventType],
    async () => await getInvitationDetails(buildId, eventType),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!buildId && emailType === EmailType.Invite && open
    }
  );

  const { isLoading: isLoadingFinalContractNotification, data: finalContractNotification } =
    useQuery<IFinalContractNotification, Error>(
      ['getFinalContractNotification', open, eventType],
      async () => await getFinalContractNotification(buildId),
      {
        notifyOnChangeProps: 'tracked',
        enabled: !!buildId && emailType === EmailType.FinalContractNotification && open
      }
    );

  const {
    isLoading: isLoadingFinalPriceContractNotification,
    data: finalPriceContractNotification
  } = useQuery<IFinalPriceContractNotification, Error>(
    ['getFinalPriceContractNotification', open, eventType],
    async () => await getFinalPriceContractNotification(buildId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!buildId && emailType === EmailType.FinalPriceContractNotification && open
    }
  );

  const { isLoading: isLoadingContractSignedNotification, data: contractSignedNotification } =
    useQuery<IContractAcceptedNotification, Error>(
      ['getContractSignedNotification', open, eventType],
      async () => await getOfferContractNotification(buildId),
      {
        notifyOnChangeProps: 'tracked',
        enabled: !!buildId && emailType === EmailType.ContractAccepted && open
      }
    );

  const { isLoading: isLoadingAddressBook, data: addressBookData } = useQuery<
    IAddressBookItem[],
    Error
  >(['addressBook', open], async () => await getAddressBookItems(buildId), {
    notifyOnChangeProps: 'tracked',
    enabled: !!buildId && open === true
  });

  const handleSubjectTitle = () => {
    if (eventType === EventType.HomeownerOrientation) {
      return 'Orientation';
    } else if (eventType === EventType.HomeownerCelebration) {
      return 'Celebration';
    } else if (eventType === EventType.ClosingAppointment) {
      return 'Closing';
    }
  };

  let endAppointmentDate = '';

  const getAppointment = () => {
    if (eventType === EventType.HomeownerOrientation) {
      endAppointmentDate = contractDates?.homeownerOrientationEnd;
      return `${
        contractDates?.homeownerOrientation
          ? formatShortFriendlyDateWithTime(contractDates?.homeownerOrientation)
          : 'N/A'
      } - ${
        contractDates?.homeownerOrientationEnd
          ? formatShortFriendlyDateWithTime(contractDates?.homeownerOrientationEnd)
          : 'N/A'
      }`;
    } else if (eventType === EventType.HomeownerCelebration) {
      endAppointmentDate = contractDates?.homeownerCelebrationEnd;
      return `${
        contractDates?.homeownerCelebration
          ? formatShortFriendlyDateWithTime(contractDates?.homeownerCelebration)
          : 'N/A'
      } - ${
        contractDates?.homeownerCelebrationEnd
          ? formatShortFriendlyDateWithTime(contractDates?.homeownerCelebrationEnd)
          : 'N/A'
      }`;
    } else if (
      eventType === EventType.ClosingAppointment &&
      !contractDates?.isClosingAppointmentAllDay
    ) {
      endAppointmentDate = contractDates?.closingAppointmentEnd;
      return `${
        contractDates?.closingAppointmentStart
          ? formatShortFriendlyDateWithTime(contractDates?.closingAppointmentStart)
          : 'N/A'
      } - ${
        contractDates?.closingAppointmentEnd
          ? formatShortFriendlyDateWithTime(contractDates?.closingAppointmentEnd)
          : 'N/A'
      }`;
    } else if (
      eventType === EventType.ClosingAppointment &&
      contractDates?.isClosingAppointmentAllDay
    ) {
      endAppointmentDate = contractDates?.closingAppointmentStart;
      return `${
        contractDates?.closingAppointmentStart
          ? formatDate(contractDates?.closingAppointmentStart)
          : 'N/A'
      }`;
    }
  };

  const generateSubjectLine = () => {
    if (invitationDetails?.subject) {
      return invitationDetails.subject;
    }
    if (invitationDetails?.clientName) {
      const subjectTitle = handleSubjectTitle();
      return `${subjectTitle} - ${invitationDetails.clientName} ${invitationDetails?.address}`;
    }
    if (finalContractNotification?.subject) {
      return finalContractNotification.subject;
    }
    if (finalPriceContractNotification?.subject) {
      return finalPriceContractNotification.subject;
    }
    if (contractSignedNotification?.subject) {
      return contractSignedNotification.subject;
    }
    return '';
  };

  const getEmailMessage = () => {
    if (invitationDetails?.body) {
      return invitationDetails.body;
    }
    if (finalContractNotification) {
      return finalContractNotification.body;
    }
    if (finalPriceContractNotification) {
      return finalPriceContractNotification.body;
    }
    if (contractSignedNotification) {
      return contractSignedNotification.body;
    }
    return '';
  };

  const getEmailAddresses = () => {
    switch (emailType) {
      case EmailType.Invite:
        return invitationDetails?.invitationDetails?.emailAddresses;

      case EmailType.FinalContractNotification:
        return finalContractNotification?.recipients;

      default:
        return contractSignedNotification?.recipients ?? [];
    }
  };
  return (
    <Formik
      enableReinitialize={true}
      validateOnBlur
      initialValues={{
        emailTemplateId: '',
        subject: invitationDetails?.invitationDetails?.subject
          ? invitationDetails?.invitationDetails?.subject
          : generateSubjectLine(),
        emailAddresses: getEmailAddresses() ?? [],
        location: invitationDetails?.invitationDetails?.location
          ? invitationDetails?.invitationDetails?.location
          : invitationDetails?.location
          ? invitationDetails?.location
          : '',
        message: invitationDetails?.invitationDetails?.body
          ? invitationDetails?.invitationDetails?.body
          : getEmailMessage(),
        emailType: emailType,
        eventType: eventType
      }}
      validationSchema={Schema}
      onSubmit={async (values, actions) => {
        const data: IUnderContractEmailPayload = {
          ...values
        };

        try {
          setIsLoading(true);
          if (emailType === EmailType.Email) {
            await sendUnderContractEmailUpdates(buildId, data);
            enqueueSnackbar('Email has been queued to send.', {
              variant: 'success'
            });
          } else if (emailType === EmailType.Invite) {
            const isUpdate = Boolean(invitationDetails?.invitationDetails?.buildEventId);
            if (isUpdate) {
              await updateUnderContractClosingInvitationEmail(buildId, data);
              enqueueSnackbar(`${handleSubjectTitle()} invitation has been updated and sent.`, {
                variant: 'success'
              });
            } else {
              await sendUnderContractClosingInvitationEmail(buildId, data);
              refetchContractDetails();
              enqueueSnackbar(`${handleSubjectTitle()} invitation has been sent.`, {
                variant: 'success'
              });
            }
          } else if (emailType === EmailType.FinalContractNotification) {
            await sendFinalContractUploadedEmail(buildId, data);
            enqueueSnackbar('Final Contract Notification has been queued to send.', {
              variant: 'success'
            });
          } else if (emailType === EmailType.FinalPriceContractNotification) {
            await sendFinalPriceContractUploadedEmail(buildId, data);
            enqueueSnackbar('Final Price Contract Notification has been queued to send.', {
              variant: 'success'
            });
          } else if (emailType === EmailType.ContractAccepted) {
            await sendOfferContractUploadedEmail(buildId, data);
            enqueueSnackbar('Contract Signed Notification has been queued to send.', {
              variant: 'success'
            });
          }
          onClose();
          actions.resetForm();
          if (selectedPage === underContractPages.clientHistory) {
            fetchClientHistory();
          }
        } catch (error: any) {
          enqueueSnackbar(error?.Detail ?? 'Error sending email, please try again.', {
            variant: 'error'
          });
        }
      }}
    >
      {({
        resetForm,
        isSubmitting,
        values,
        touched,
        setTouched,
        errors,
        handleBlur,
        setFieldValue,
        handleSubmit,
        isValid,
        validateForm,
        initialValues
      }) => {
        const handleClose = (shouldRefetch?: boolean) => {
          // disregard check for emailTemplateId since it is set to empty for its initial value
          // but then in a child component it is set upon load
          // if it changes other fields like subject and message will change as well
          const { emailTemplateId: initialEmailTempalteId, ...rest } = initialValues;
          const { emailTemplateId, ...newValues } = values;
          if (!deepEqual(rest, newValues)) {
            const result = window.confirm(
              'You have unsaved changes, are you sure you want to exit?'
            );
            if (result) {
              onClose(shouldRefetch);
              resetForm();
            } else {
              return;
            }
          } else {
            onClose(shouldRefetch);
            resetForm();
          }
        };
        return (
          <UnderContractEmailModalContent
            open={open && !isLoading}
            handleClose={handleClose}
            setFieldValue={setFieldValue}
            values={values}
            touched={touched}
            setTouched={setTouched}
            errors={errors}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isLoadingModalContent={isLoading}
            isFormDisabled={isSubmitting || isLoading}
            recipientOptions={addressBookData}
            areRecipientsLoading={isLoadingAddressBook}
            emailType={emailType}
            validateForm={validateForm}
            isLoadingInvitationDetails={
              isLoadingInvitationDetails ||
              isLoadingFinalContractNotification ||
              isLoadingFinalPriceContractNotification ||
              isLoadingContractSignedNotification
            }
            eventType={eventType}
            appointment={getAppointment()}
            buildId={buildId}
            showCancelButton={
              invitationDetails?.invitationDetails?.buildEventId &&
              !isPast(new Date(endAppointmentDate))
                ? true
                : false
            }
          />
        );
      }}
    </Formik>
  );
};
